import { Subscription } from "@/context/SubscriptionContext";
import { findServiceAlternatives } from "@/lib/alternatives";

export type Insight = {
  id: string;
  title: string;
  description: string;
  type: "danger" | "warning" | "action" | "success";
  savings: number;
  targetId?: string; 
  targetName?: string;
  actionText: string;
};

export type SubscriptionIntelligence = {
  id: string;
  name: string;
  inferredCategory: string;
  lifecycleStage: "trial" | "active" | "declining" | "waste" | "paused";
  monthlyCost: number;
  annualCost: number;
  daysUntilRenewal: number | null;
  usageScore: number;
  costEfficiencyScore: number;
  necessityScore: number;
  replacementRiskScore: number;
  valueScore: number;
  renewalRisk: number;
  wasteRisk: number;
  regretScore: number;
  priorityScore: number;
  stabilityScore: number;
  usageConfidence: number;
  trialDanger: boolean;
  goalFulfillment: number;
  timeToValue: number;
  emotionalCost: number;
  recommendation: "keep" | "review" | "downgrade" | "cancel";
  reason: string;
};

export type OverlapCluster = {
  category: string;
  services: SubscriptionIntelligence[];
  monthlyCost: number;
  suggestedTarget?: SubscriptionIntelligence;
};

export type CategorySpend = {
  category: string;
  monthlySpend: number;
  annualSpend: number;
};

export type ForecastPoint = {
  month: string;
  spend: number;
};

export type AlternativePlay = {
  id: string;
  targetName: string;
  replacementName: string;
  category: string;
  monthlySavings: number;
  rationale: string;
};

export type KnowledgeGraphEdge = {
  id: string;
  from: string;
  to: string;
  relation: "overlaps" | "replaces" | "depends";
  rationale: string;
};

export type StoryMoment = {
  id: string;
  title: string;
  body: string;
  tone: "calm" | "watch" | "tense";
};

export type FutureSelfProjection = {
  twelveMonthSpend: number;
  optimizedSpend: number;
  possibleSavings: number;
  lifestyleImpact: string;
};

export type StackAnomaly = {
  id: string;
  type: "price_increase" | "category_spike" | "renewal_cluster" | "usage_drop";
  title: string;
  description: string;
  severity: "low" | "medium" | "high";
};

export type BundleOpportunity = {
  id: string;
  title: string;
  products: string[];
  estimatedMonthlySavings: number;
  rationale: string;
};

export type StackAnalysis = {
  algorithmVersion: string;
  totalMonthlySpend: number;
  quarterlySpend: number;
  totalYearlySpend: number;
  projectedTwelveMonthSpend: number;
  averageValueScore: number;
  wasteMonthlySpend: number;
  wastePercentage: number;
  cleanlinessScore: number;
  densityScore: number;
  personality: "Explorer" | "Minimalist" | "Hoarder";
  pulseState: "calm" | "watch" | "tense";
  primaryChallenge: string;
  subscriptions: SubscriptionIntelligence[];
  reviewQueue: SubscriptionIntelligence[];
  overlaps: OverlapCluster[];
  categorySpend: CategorySpend[];
  forecast: ForecastPoint[];
  alternatives: AlternativePlay[];
  knowledgeGraph: KnowledgeGraphEdge[];
  story: StoryMoment[];
  futureSelf: FutureSelfProjection;
  anomalies: StackAnomaly[];
  bundleOpportunities: BundleOpportunity[];
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const roundCurrency = (value: number) => Number(value.toFixed(2));

const monthlyCost = (sub: Subscription) => sub.cycle === "Yearly" ? sub.price / 12 : sub.price;

const annualCost = (sub: Subscription) => monthlyCost(sub) * 12;

const toCurrency = (value: number) => `$${value.toFixed(2)}`;

const inferCategory = (sub: Subscription) => {
  const explicitCategory = sub.category.trim();
  const text = `${sub.name} ${sub.plan} ${explicitCategory}`.toLowerCase();

  if (/(chatgpt|claude|gemini|perplexity|midjourney|runway|copilot|\bai\b|\bllm\b)/.test(text)) return "AI & Tools";
  if (/(netflix|spotify|youtube|hulu|disney|prime|max|apple tv|entertainment|music|stream)/.test(text)) return "Entertainment";
  if (/(notion|evernote|asana|todoist|monday|productivity|office|workspace|calendar|email)/.test(text)) return "Productivity";
  if (/(github|vercel|linear|slack|cursor|jetbrains|dev|code|hosting|database)/.test(text)) return "Development";
  if (/(figma|adobe|canva|design|framer|sketch|creative)/.test(text)) return "Design";
  if (/(dropbox|google one|icloud|storage|drive|backup)/.test(text)) return "Storage";

  return explicitCategory || "Software";
};

const categoryBenchmarks: Record<string, number> = {
  "AI & Tools": 25,
  Entertainment: 18,
  Development: 32,
  Design: 28,
  Productivity: 22,
  Storage: 12,
  Software: 20,
};

const categoryFeatureMap: Record<string, string[]> = {
  "AI & Tools": ["ai_chat", "ai_coding", "ai_research", "file_upload", "advanced_reasoning"],
  Entertainment: ["music", "video", "streaming", "offline_access"],
  Development: ["developer_tool", "hosting", "collaboration", "automation"],
  Design: ["design", "brand_tools", "image_generation", "collaboration"],
  Productivity: ["docs", "tasks", "calendar", "workspace"],
  Storage: ["cloud_storage", "file_sync", "backup"],
  Software: ["workflow", "account", "subscription"],
};

const daysUntil = (value: string) => {
  const parsed = parseBillingDate(value);
  if (!parsed) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  parsed.setHours(0, 0, 0, 0);
  return Math.ceil((parsed.getTime() - today.getTime()) / 86_400_000);
};

const getUsageScore = (sub: Subscription) => {
  if (sub.status === "paused") return 8;
  if (sub.status === "review") return 30;
  if (sub.status === "warning") return 38;
  return 72;
};

const getCostEfficiencyScore = (cost: number, category: string) => {
  const benchmark = categoryBenchmarks[category] || categoryBenchmarks.Software;
  if (cost <= 0) return 100;
  return clamp(Math.round(105 - (cost / benchmark) * 32), 0, 100);
};

const getNecessityScore = (sub: Subscription, category: string, overlapCount: number, cost: number) => {
  const base = ["Development", "Productivity", "AI & Tools"].includes(category) ? 74 : category === "Storage" ? 68 : 56;
  const statusPenalty = sub.status === "review" ? 18 : sub.status === "warning" ? 12 : sub.status === "paused" ? 34 : 0;
  const overlapPenalty = overlapCount > 1 ? Math.min(22, (overlapCount - 1) * 9) : 0;
  const costPenalty = cost > 80 ? 8 : 0;
  return clamp(Math.round(base - statusPenalty - overlapPenalty - costPenalty), 0, 100);
};

const getReplacementRiskScore = (category: string, overlapCount: number, cost: number) => {
  const overlapRisk = overlapCount > 1 ? 35 + (overlapCount - 2) * 15 : 10;
  const commodityRisk = ["Entertainment", "Storage", "AI & Tools", "Design"].includes(category) ? 18 : 8;
  const benchmark = categoryBenchmarks[category] || categoryBenchmarks.Software;
  const costRisk = cost > benchmark * 1.5 ? 14 : 0;
  return clamp(Math.round(overlapRisk + commodityRisk + costRisk), 0, 100);
};

const getLifecycleStage = (
  sub: Subscription,
  usageScore: number,
  cost: number,
  replacementRiskScore: number,
): SubscriptionIntelligence["lifecycleStage"] => {
  if (sub.status === "paused") return "paused";
  if (/trial/i.test(`${sub.name} ${sub.plan}`) || sub.trialEndsAt) return "trial";
  if (usageScore >= 66) return "active";
  if (usageScore < 35 && cost >= 20 && replacementRiskScore >= 45) return "waste";
  return "declining";
};

const getRenewalRisk = (
  cost: number,
  usageScore: number,
  replacementRiskScore: number,
  daysUntilRenewal: number | null,
) => {
  const renewalSoon = daysUntilRenewal === null ? 20 : daysUntilRenewal <= 3 ? 100 : daysUntilRenewal <= 7 ? 82 : daysUntilRenewal <= 14 ? 58 : 24;
  const highPrice = clamp((cost / 120) * 100, 0, 100);
  const lowUsage = 100 - usageScore;
  return clamp(Math.round(lowUsage * 0.34 + highPrice * 0.22 + renewalSoon * 0.24 + replacementRiskScore * 0.2), 0, 100);
};

const getRecommendation = (valueScore: number, renewalRisk: number): SubscriptionIntelligence["recommendation"] => {
  if (valueScore < 42 || renewalRisk >= 82) return "cancel";
  if (valueScore < 58 || renewalRisk >= 66) return "downgrade";
  if (valueScore < 74 || renewalRisk >= 45) return "review";
  return "keep";
};

const getReason = (
  sub: Subscription,
  inferredCategory: string,
  overlapCount: number,
  valueScore: number,
  wasteRisk: number,
) => {
  const cost = monthlyCost(sub);

  if (sub.status === "review") return "Already marked for review. Make the decision before the next renewal.";
  if (sub.status === "warning") return "The account is showing warning signals. Confirm it still earns its place.";
  if (overlapCount > 1) return `Part of a ${inferredCategory} cluster with ${overlapCount} active services.`;
  if (cost >= 80) return `${toCurrency(cost)}/mo is heavy enough to require visible ROI.`;
  if (wasteRisk >= 50) return "Low confidence value signal. Ask whether it is helping this month.";
  if (valueScore >= 82) return "Strong fit. Keep it unless a cheaper bundle appears.";
  return "Stable, but worth checking against cheaper alternatives.";
};

const getMonthLabel = (date: Date) => date.toLocaleString("en-US", { month: "short" });

const parseBillingDate = (value: string) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const buildForecast = (subs: Subscription[]): ForecastPoint[] => {
  const start = new Date();
  start.setDate(1);

  return Array.from({ length: 12 }, (_, index) => {
    const current = new Date(start);
    current.setMonth(start.getMonth() + index);
    const currentMonth = current.getMonth();
    const currentYear = current.getFullYear();

    const spend = subs.reduce((sum, sub) => {
      const nextBill = parseBillingDate(sub.nextBill);
      if (!nextBill) return sum + monthlyCost(sub);

      if (sub.cycle === "Monthly") {
        return nextBill <= current || (nextBill.getMonth() === currentMonth && nextBill.getFullYear() === currentYear)
          ? sum + sub.price
          : sum;
      }

      return nextBill.getMonth() === currentMonth && nextBill.getFullYear() === currentYear ? sum + sub.price : sum;
    }, 0);

    return {
      month: getMonthLabel(current),
      spend: Number(spend.toFixed(2)),
    };
  });
};

const alternativeByCategory: Record<string, string[]> = {
  "AI & Tools": ["Claude Pro", "Perplexity Pro", "Gemini Advanced"],
  Entertainment: ["YouTube Premium", "Spotify Duo", "Annual bundle"],
  Development: ["GitHub Team", "Vercel Pro", "Cloudflare bundle"],
  Design: ["Figma Professional", "Canva Pro", "Adobe single-app plan"],
  Productivity: ["Notion Plus", "Google Workspace", "Microsoft 365"],
  Storage: ["Google One", "iCloud+", "Dropbox Essentials"],
};

const buildAlternatives = (items: SubscriptionIntelligence[], overlaps: OverlapCluster[]): AlternativePlay[] => {
  const overlapTargets = overlaps.flatMap((overlap) => {
    if (!overlap.suggestedTarget) return [];
    const replacement = overlap.services.find((service) => service.id !== overlap.suggestedTarget?.id);
    return [{
      id: `alt-overlap-${overlap.suggestedTarget.id}`,
      targetName: overlap.suggestedTarget.name,
      replacementName: replacement?.name || alternativeByCategory[overlap.category]?.[0] || "a leaner plan",
      category: overlap.category,
      monthlySavings: overlap.suggestedTarget.monthlyCost,
      rationale: `${overlap.category} already has ${overlap.services.length} tools. Keep the strongest one and cut the weakest duplicate.`,
    }];
  });

  const downgradeTargets = items
    .filter((item) => item.recommendation === "downgrade" || item.recommendation === "review")
    .slice(0, 3)
    .map((item) => ({
      id: `alt-downgrade-${item.id}`,
      targetName: item.name,
      replacementName: findServiceAlternatives(item.name, item.inferredCategory)[0]?.alternative || alternativeByCategory[item.inferredCategory]?.[0] || "a lower tier",
      category: item.inferredCategory,
      monthlySavings: Number((findServiceAlternatives(item.name, item.inferredCategory)[0]?.estimatedMonthlySavings || item.monthlyCost * 0.35).toFixed(2)),
      rationale: findServiceAlternatives(item.name, item.inferredCategory)[0]?.rationale || `${item.name} does not need to disappear yet. First test a cheaper tier or a narrower replacement.`,
    }));

  return [...overlapTargets, ...downgradeTargets]
    .filter((play, index, plays) => plays.findIndex((candidate) => candidate.targetName === play.targetName) === index)
    .sort((a, b) => b.monthlySavings - a.monthlySavings)
    .slice(0, 5);
};

const buildKnowledgeGraph = (overlaps: OverlapCluster[], items: SubscriptionIntelligence[]): KnowledgeGraphEdge[] => {
  const overlapEdges = overlaps.flatMap((overlap) => {
    const anchor = overlap.services[0];
    return overlap.services.slice(1).map((service) => ({
      id: `edge-${anchor.id}-${service.id}`,
      from: anchor.name,
      to: service.name,
      relation: "overlaps" as const,
      rationale: `Both live in ${overlap.category}; Orizonte should force a single purpose for each.`,
    }));
  });

  const categoryLeaderEdges = Object.values(items.reduce((acc, item) => {
    acc[item.inferredCategory] = acc[item.inferredCategory] || [];
    acc[item.inferredCategory].push(item);
    return acc;
  }, {} as Record<string, SubscriptionIntelligence[]>)).flatMap((categoryItems) => {
    if (categoryItems.length < 2) return [];
    const sorted = [...categoryItems].sort((a, b) => b.valueScore - a.valueScore);
    const leader = sorted[0];
    return sorted.slice(1, 3).map((item) => ({
      id: `replace-${leader.id}-${item.id}`,
      from: leader.name,
      to: item.name,
      relation: "replaces" as const,
      rationale: `${leader.name} has the strongest value signal in this cluster.`,
    }));
  });

  return [...overlapEdges, ...categoryLeaderEdges]
    .filter((edge, index, edges) => edges.findIndex((candidate) => candidate.id === edge.id) === index)
    .slice(0, 6);
};

const buildStory = (
  items: SubscriptionIntelligence[],
  categorySpend: CategorySpend[],
  wastePercentage: number,
  projectedTwelveMonthSpend: number,
): StoryMoment[] => {
  const dominantCategory = categorySpend[0];
  const weakest = items[0];
  const strongest = [...items].sort((a, b) => b.valueScore - a.valueScore)[0];

  return [
    dominantCategory && {
      id: "dominant-phase",
      title: `${dominantCategory.category} phase`,
      body: `Your stack currently leans toward ${dominantCategory.category}, burning ${toCurrency(dominantCategory.monthlySpend)}/mo there.`,
      tone: dominantCategory.monthlySpend > 80 ? "watch" : "calm",
    },
    weakest && {
      id: "weakest-signal",
      title: "Weakest signal",
      body: `${weakest.name} is where Orizonte should be blunt: ${weakest.valueScore}/100 is not a free pass.`,
      tone: weakest.valueScore < 58 ? "tense" : "watch",
    },
    strongest && {
      id: "keeper",
      title: "Earned its place",
      body: `${strongest.name} has the strongest value signal. Keep it unless your workflow changes.`,
      tone: "calm",
    },
    {
      id: "future-pressure",
      title: "Future pressure",
      body: `At the current pace, the next 12 months point toward ${toCurrency(projectedTwelveMonthSpend)}.`,
      tone: wastePercentage >= 25 ? "tense" : "watch",
    },
  ].filter(Boolean) as StoryMoment[];
};

export const analyzeSubscriptionStack = (subs: Subscription[]): StackAnalysis => {
  const categoryCounts = subs.reduce((acc, sub) => {
    const category = inferCategory(sub);
    acc[category] = (acc[category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const intelligence = subs.map((sub) => {
    const inferredCategory = inferCategory(sub);
    const cost = monthlyCost(sub);
    const overlapCount = categoryCounts[inferredCategory] || 1;
    const usageScore = getUsageScore(sub);
    const costEfficiencyScore = getCostEfficiencyScore(cost, inferredCategory);
    const necessityScore = getNecessityScore(sub, inferredCategory, overlapCount, cost);
    const replacementRiskScore = getReplacementRiskScore(inferredCategory, overlapCount, cost);
    const valueScore = clamp(Math.round(
      usageScore * 0.35 +
      costEfficiencyScore * 0.25 +
      necessityScore * 0.25 +
      (100 - replacementRiskScore) * 0.15
    ), 8, 98);
    const daysUntilRenewal = daysUntil(sub.nextBill);
    const renewalRisk = getRenewalRisk(cost, usageScore, replacementRiskScore, daysUntilRenewal);
    const wasteRisk = clamp(Math.round(renewalRisk * 0.7 + (100 - valueScore) * 0.3), 0, 100);
    const highPrice = clamp((cost / 120) * 100, 0, 100);
    const lowUsage = 100 - usageScore;
    const regretScore = clamp(Math.round(lowUsage * 0.4 + highPrice * 0.3 + replacementRiskScore * 0.2 + (sub.status === "review" ? 10 : 0)), 0, 100);
    const renewalSoon = daysUntilRenewal === null ? 20 : daysUntilRenewal <= 3 ? 100 : daysUntilRenewal <= 7 ? 82 : daysUntilRenewal <= 14 ? 58 : 24;
    const priorityScore = clamp(Math.round(highPrice * 0.25 + renewalSoon * 0.25 + lowUsage * 0.25 + replacementRiskScore * 0.25), 0, 100);
    const stabilityScore = clamp(Math.round((100 - replacementRiskScore) * 0.35 + costEfficiencyScore * 0.3 + usageScore * 0.35), 0, 100);
    const usageConfidence = clamp(Math.round((sub.usageConfidence ?? (sub.status === "active" ? 0.45 : sub.status === "paused" ? 0.68 : 0.52)) * 100), 0, 100);
    const daysUntilTrialEnds = sub.trialEndsAt ? daysUntil(sub.trialEndsAt) : null;
    const trialDanger = (/trial/i.test(`${sub.name} ${sub.plan}`) || Boolean(sub.trialEndsAt))
      && (daysUntilTrialEnds ?? daysUntilRenewal ?? 999) <= 3
      && usageScore < 50;
    const goalFulfillment = clamp(Math.round(usageScore * 0.55 + necessityScore * 0.45), 0, 100);
    const timeToValue = clamp(Math.round((usageScore / Math.max(cost, 1)) * 20), 0, 100);
    const emotionalCost = clamp(Math.round(regretScore * 0.45 + highPrice * 0.25 + renewalRisk * 0.3), 0, 100);
    const lifecycleStage = getLifecycleStage(sub, usageScore, cost, replacementRiskScore);
    const recommendation = getRecommendation(valueScore, renewalRisk);

    return {
      id: sub.id,
      name: sub.name,
      inferredCategory,
      lifecycleStage,
      monthlyCost: Number(cost.toFixed(2)),
      annualCost: Number(annualCost(sub).toFixed(2)),
      daysUntilRenewal,
      usageScore,
      costEfficiencyScore,
      necessityScore,
      replacementRiskScore,
      valueScore,
      renewalRisk,
      wasteRisk,
      regretScore,
      priorityScore,
      stabilityScore,
      usageConfidence,
      trialDanger,
      goalFulfillment,
      timeToValue,
      emotionalCost,
      recommendation,
      reason: getReason(sub, inferredCategory, overlapCount, valueScore, wasteRisk),
    };
  });

  const totalMonthlySpend = intelligence.reduce((sum, sub) => sum + sub.monthlyCost, 0);
  const totalYearlySpend = totalMonthlySpend * 12;
  const riskySpend = intelligence.reduce((sum, sub) => {
    if (sub.recommendation === "cancel") return sum + sub.monthlyCost;
    if (sub.recommendation === "downgrade") return sum + sub.monthlyCost * 0.45;
    if (sub.recommendation === "review") return sum + sub.monthlyCost * 0.2;
    return sum;
  }, 0);

  const groups = intelligence.reduce((acc, sub) => {
    acc[sub.inferredCategory] = acc[sub.inferredCategory] || [];
    acc[sub.inferredCategory].push(sub);
    return acc;
  }, {} as Record<string, SubscriptionIntelligence[]>);

  const overlaps = Object.entries(groups)
    .filter(([, services]) => services.length > 1)
    .map(([category, services]) => {
      const sorted = [...services].sort((a, b) => a.valueScore - b.valueScore || b.monthlyCost - a.monthlyCost);
      return {
        category,
        services: sorted,
        monthlyCost: Number(services.reduce((sum, sub) => sum + sub.monthlyCost, 0).toFixed(2)),
        suggestedTarget: sorted[0],
      };
    })
    .sort((a, b) => b.monthlyCost - a.monthlyCost);

  const categorySpend = Object.entries(groups)
    .map(([category, services]) => {
      const monthlySpend = services.reduce((sum, sub) => sum + sub.monthlyCost, 0);
      return {
        category,
        monthlySpend: Number(monthlySpend.toFixed(2)),
        annualSpend: Number((monthlySpend * 12).toFixed(2)),
      };
    })
    .sort((a, b) => b.monthlySpend - a.monthlySpend);

  const forecast = buildForecast(subs);
  const projectedTwelveMonthSpend = forecast.reduce((sum, point) => sum + point.spend, 0);
  const averageValueScore = intelligence.length
    ? Math.round(intelligence.reduce((sum, sub) => sum + sub.valueScore, 0) / intelligence.length)
    : 0;
  const wastePercentage = totalMonthlySpend > 0 ? Math.round((riskySpend / totalMonthlySpend) * 100) : 0;
  const categoriesUsed = Math.max(1, Object.keys(groups).length);
  const densityScore = clamp(Math.round((intelligence.length / categoriesUsed) * 24), 0, 100);
  const cleanlinessScore = intelligence.length === 0
    ? 100
    : clamp(Math.round(100 - wastePercentage * 0.8 - densityScore * 0.25 - Math.max(0, 72 - averageValueScore) * 0.6), 0, 100);
  const personality: StackAnalysis["personality"] = intelligence.length <= 4 && cleanlinessScore >= 70
    ? "Minimalist"
    : densityScore >= 65 || wastePercentage >= 28
      ? "Hoarder"
      : "Explorer";
  const pulseState: StackAnalysis["pulseState"] = intelligence.length === 0
    ? "calm"
    : wastePercentage >= 35 || averageValueScore < 58
    ? "tense"
    : wastePercentage >= 18 || averageValueScore < 74
      ? "watch"
      : "calm";
  const lowestScore = [...intelligence].sort((a, b) => a.valueScore - b.valueScore)[0];
  const sortedIntelligence = [...intelligence].sort((a, b) => a.valueScore - b.valueScore);
  const reviewQueue = [...intelligence].sort((a, b) => b.priorityScore - a.priorityScore || a.valueScore - b.valueScore);
  const alternatives = buildAlternatives(sortedIntelligence, overlaps);
  const knowledgeGraph = buildKnowledgeGraph(overlaps, sortedIntelligence);
  const possibleSavings = Math.max(roundCurrency(alternatives.reduce((sum, play) => sum + play.monthlySavings, 0) * 12), roundCurrency(riskySpend * 12));
  const optimizedSpend = Math.max(0, Number((projectedTwelveMonthSpend - possibleSavings).toFixed(2)));
  const story = buildStory(sortedIntelligence, categorySpend, wastePercentage, projectedTwelveMonthSpend);
  const bundleOpportunities = overlaps.slice(0, 4).map((overlap) => ({
    id: `bundle-${overlap.category}`,
    title: `${overlap.category} consolidation`,
    products: overlap.services.map((service) => service.name),
    estimatedMonthlySavings: roundCurrency((overlap.suggestedTarget?.monthlyCost || overlap.monthlyCost * 0.25)),
    rationale: `Replace the weakest duplicate in ${overlap.category} or collapse the group into one bundle/plan.`,
  }));
  const renewalCluster = intelligence.filter((item) => item.daysUntilRenewal !== null && item.daysUntilRenewal >= 0 && item.daysUntilRenewal <= 7);
  const anomalies: StackAnomaly[] = [
    ...intelligence
      .filter((item) => item.lifecycleStage === "declining" && item.usageScore < 45)
      .slice(0, 3)
      .map((item) => ({
        id: `usage-drop-${item.id}`,
        type: "usage_drop" as const,
        title: `${item.name} usage signal is weak`,
        description: `${item.name} has a usage score of ${item.usageScore}/100 and a ${item.renewalRisk}/100 renewal risk.`,
        severity: item.renewalRisk >= 70 ? "high" as const : "medium" as const,
      })),
    ...(renewalCluster.length >= 3 ? [{
      id: "renewal-cluster",
      type: "renewal_cluster" as const,
      title: "Renewal cluster ahead",
      description: `${renewalCluster.length} subscriptions renew inside seven days.`,
      severity: "high" as const,
    }] : []),
  ];

  return {
    algorithmVersion: "orizonte-decision-engine-v1.0.0",
    totalMonthlySpend: Number(totalMonthlySpend.toFixed(2)),
    quarterlySpend: Number((totalMonthlySpend * 3).toFixed(2)),
    totalYearlySpend: Number(totalYearlySpend.toFixed(2)),
    projectedTwelveMonthSpend: Number(projectedTwelveMonthSpend.toFixed(2)),
    averageValueScore,
    wasteMonthlySpend: Number(riskySpend.toFixed(2)),
    wastePercentage,
    cleanlinessScore,
    densityScore,
    personality,
    pulseState,
    primaryChallenge: lowestScore
      ? `${lowestScore.name} is the weakest signal at ${lowestScore.valueScore}/100.`
      : "Add subscriptions to activate Orizonte's intelligence layer.",
    subscriptions: sortedIntelligence,
    reviewQueue,
    overlaps,
    categorySpend,
    forecast,
    alternatives,
    knowledgeGraph,
    story,
    futureSelf: {
      twelveMonthSpend: Number(projectedTwelveMonthSpend.toFixed(2)),
      optimizedSpend,
      possibleSavings: Number(possibleSavings.toFixed(2)),
      lifestyleImpact: possibleSavings > 1200
        ? "A serious yearly reset, not just a tidy-up."
        : possibleSavings > 300
          ? "Enough to feel in your budget without changing your life."
          : "Mostly maintenance. Your bigger win is preventing future creep.",
    },
    anomalies,
    bundleOpportunities,
  };
};

export const generateInsights = (subs: Subscription[]): Insight[] => {
  const insights: Insight[] = [];
  const analysis = analyzeSubscriptionStack(subs);

  // Logic 1: Overlap Detection - same category with multiple subscriptions
  for (const overlap of analysis.overlaps) {
    const target = overlap.suggestedTarget;
    insights.push({
      id: `overlap-${overlap.category}`,
      title: `${overlap.category} Stack Overlap`,
      description: `You have ${overlap.services.length} subscriptions in ${overlap.category}. Orizonte would challenge ${target?.name || "the weakest service"} first while the cluster burns ${toCurrency(overlap.monthlyCost)}/mo.`,
      type: 'action',
      savings: target ? target.annualCost : overlap.monthlyCost * 12 * 0.2,
      targetId: target?.id,
      targetName: target?.name,
      actionText: 'Review Stack'
    });
  }

  // Logic 2: High spend warning - single subscription over $100/month
  const highSpend = subs.find(s => (s.cycle === 'Yearly' ? s.price / 12 : s.price) > 100);
  if (highSpend) {
    const monthly = highSpend.cycle === 'Yearly' ? highSpend.price / 12 : highSpend.price;
    insights.push({
      id: `high-spend-${highSpend.id}`,
      title: 'High Monthly Outlier',
      description: `${highSpend.name} costs $${monthly.toFixed(2)}/mo, which is significantly above your average. Consider reviewing the plan tier.`,
      type: 'warning',
      savings: highSpend.cycle === 'Yearly' ? highSpend.price * 0.2 : highSpend.price * 12 * 0.2,
      targetId: highSpend.id,
      targetName: highSpend.name,
      actionText: 'Review Plan'
    });
  }

  const weakestSub = analysis.subscriptions[0];
  if (weakestSub && weakestSub.valueScore < 62) {
    insights.push({
      id: `value-score-${weakestSub.id}`,
      title: `Low Value Score: ${weakestSub.name}`,
      description: `${weakestSub.name} scores ${weakestSub.valueScore}/100. ${weakestSub.reason} Orizonte would ${weakestSub.recommendation} unless you can justify the value.`,
      type: weakestSub.recommendation === "cancel" ? "danger" : "warning",
      savings: weakestSub.recommendation === "cancel" ? weakestSub.annualCost : weakestSub.annualCost * 0.45,
      targetId: weakestSub.id,
      targetName: weakestSub.name,
      actionText: weakestSub.recommendation === "cancel" ? "Challenge It" : "Inspect Value"
    });
  }

  if (analysis.wastePercentage >= 18) {
    insights.push({
      id: "waste-meter",
      title: `${analysis.wastePercentage}% Waste Risk`,
      description: `Your stack has roughly ${toCurrency(analysis.wasteMonthlySpend)}/mo in questionable spend. That is where Orizonte should intervene before adding more services.`,
      type: analysis.wastePercentage >= 35 ? "danger" : "warning",
      savings: analysis.wasteMonthlySpend * 12,
      actionText: "Open Focus Mode"
    });
  }

  // Logic 3: Yearly subscriptions (cash flow impact)
  const yearlySubs = subs.filter(s => s.cycle === 'Yearly');
  if (yearlySubs.length > 0) {
    const totalYearly = yearlySubs.reduce((sum, s) => sum + s.price, 0);
    insights.push({
      id: 'yearly-cashflow',
      title: 'Annual Commitment Review',
      description: `You have ${yearlySubs.length} annual subscription${yearlySubs.length === 1 ? '' : 's'} totaling $${totalYearly.toFixed(2)}/year. Annual plans often have better rates but lock in cash flow.`,
      type: 'action',
      savings: totalYearly * 0.1,
      actionText: 'Review Renewals'
    });
  }

  if (analysis.projectedTwelveMonthSpend > analysis.totalYearlySpend * 1.15) {
    insights.push({
      id: "forecast-shock",
      title: "Forecast Shock Detected",
      description: `Renewal timing pushes the next 12 months toward ${toCurrency(analysis.projectedTwelveMonthSpend)}. Smooth or cancel the worst offenders before the spike arrives.`,
      type: "warning",
      savings: analysis.wasteMonthlySpend * 12,
      actionText: "Inspect Forecast"
    });
  }

  // Logic 4: Large stack audit (many subscriptions)
  if (subs.length >= 5 && insights.length === 0) {
    const totalYearly = subs.reduce((acc, s) => acc + (s.cycle === 'Yearly' ? s.price : s.price * 12), 0);
    insights.push({
      id: 'stack-audit',
      title: 'Stack Audit Recommended',
      description: `Your active stack has ${subs.length} services with an annual burn of $${totalYearly.toFixed(2)}. A periodic review helps catch drift.`,
      type: 'action',
      savings: totalYearly * 0.1,
      actionText: 'Run Review'
    });
  }

  return insights;
};
